import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = signed out, obj = signed in
  const [ready, setReady] = useState(false);

  // `api`, `setUser`, `setReady` are stable — but declare deps explicitly for lint.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Not authenticated is expected on first load; log for visibility.
      if (err?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.error("Auth refresh failed:", err);
      }
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Logout error (continuing anyway):", err);
    }
    setUser(false);
  };

  const kidSignIn = async (studentId, pin) => {
    const { data } = await api.post("/kid/signin", { student_id: studentId, pin });
    await refresh();
    return data;
  };

  const kidLookup = async (email) => {
    const { data } = await api.get("/kid/lookup", { params: { email } });
    return data.students || [];
  };

  const giveConsent = async () => {
    await api.post("/auth/consent");
    setUser((u) => (u ? { ...u, consent_given: true } : u));
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, giveConsent, refresh, kidSignIn, kidLookup }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export { formatApiErrorDetail };
